import React from "react";
import { AddPhotoAlternate as ImagePlus, Autorenew as Loader2 } from "@mui/icons-material";
import styles from "../AiImageGenerator.module.css";
import { EXPORT_FORMATS } from "../../../../utils/imageEditor";
import ErrorBanner from "./ErrorBanner";

export function GeneratePanel({
	error,
	genPrompt,
	setGenPrompt,
	genSeed,
	setGenSeed,
	catalogPromptFromProduct,
	categoryPrompts,
	matchedCategory,
	useExportAndResolution,
	setUseExportAndResolution,
	exportFormat,
	setExportFormat,
	exportQuality,
	setExportQuality,
	generateDisabled,
	loading,
	onGenerate,
}) {
	const showCatalogPromptHint =
		catalogPromptFromProduct &&
		genPrompt.trim() !== catalogPromptFromProduct.trim();

	return (
		<>
			<div className={styles.panelCard}>
				<h2 className={styles.panelHeading}>
					<ImagePlus sx={{ fontSize: 16, color: "var(--aig-dark)" }} />
					Generate from prompt
				</h2>
				<p className={styles.featureNote}>
					Describe the image you want. No upload needed.
				</p>
				<div className={styles.fieldGroup}>
					<div>
						<label className={styles.label}>
							Prompt <span className={styles.labelMuted}>(required)</span>
						</label>
						<textarea
							value={genPrompt}
							onChange={(e) => setGenPrompt(e.target.value)}
							rows={6}
							placeholder="Describe the image you want to generate…"
							className={`${styles.inputNoResize} ${styles.promptTextarea}`}
						/>
						{showCatalogPromptHint && (
							<div className={styles.catalogPromptHint}>
								<label className={styles.labelSpaced}>
									Prompt from title & description
								</label>
								<p className={styles.catalogPromptText}>{catalogPromptFromProduct}</p>
								<button
									type="button"
									onClick={() => setGenPrompt(catalogPromptFromProduct)}
									className={styles.promptChip}
								>
									Use this prompt
								</button>
							</div>
						)}
					</div>

					{categoryPrompts.length > 0 && (
						<div className={styles.suggestedPromptsWrap}>
							<label className={styles.labelSpaced}>
								Suggested prompts for{" "}
								<span className={styles.labelBold}>{matchedCategory}</span>
							</label>
							<div className={styles.promptChipsScroll}>
								<div className={styles.promptChips}>
									{categoryPrompts.map((prompt, idx) => {
										const label =
											prompt.length > 60 ? prompt.slice(0, 60) + "…" : prompt;
										return (
											<button
												key={idx}
												type="button"
												onClick={() => setGenPrompt(prompt)}
												className={styles.promptChip}
											>
												{label.charAt(0).toUpperCase() + label.slice(1)}
											</button>
										);
									})}
								</div>
							</div>
						</div>
					)}

					<div>
						<label className={styles.label}>
							Seed{" "}
							<span className={styles.labelMuted}>
								(optional, for reproducibility)
							</span>
						</label>
						<input
							type="number"
							value={genSeed}
							onChange={(e) => setGenSeed(e.target.value)}
							placeholder="e.g. 12345"
							className={styles.input}
						/>
					</div>
				</div>
			</div>

			<div className={styles.panelCard}>
				<label className={styles.genExportCheckLabel}>
					<input
						type="checkbox"
						checked={useExportAndResolution}
						onChange={(e) => setUseExportAndResolution(e.target.checked)}
						className={styles.checkboxAccent}
					/>
					<span className={styles.genExportCheckTitle}>Custom export format</span>
				</label>
				{useExportAndResolution && (
					<div className={styles.genExportOptions}>
						<select
							value={exportFormat}
							onChange={(e) => setExportFormat(e.target.value)}
							className={styles.input}
						>
							{EXPORT_FORMATS.map((f) => (
								<option key={f.value} value={f.value}>
									{f.label}
								</option>
							))}
						</select>
						{exportFormat === "jpeg" && (
							<div>
								<label className={styles.exportQualityLabel}>
									Quality: {exportQuality}
								</label>
								<input
									type="range"
									min="1"
									max="100"
									value={exportQuality}
									onChange={(e) => setExportQuality(Number(e.target.value))}
									className={styles.rangeAccent}
								/>
							</div>
						)}
					</div>
				)}
			</div>
			{error && <ErrorBanner message={error} />}
			<button
				type="button"
				onClick={onGenerate}
				disabled={generateDisabled}
				className={styles.primaryBtn}
			>
				{loading ? (
					<>
						<Loader2 sx={{ fontSize: 20 }} className={styles.spin} />
						Generating…
					</>
				) : (
					<>
						<ImagePlus sx={{ fontSize: 20 }} />
						Generate image
					</>
				)}
			</button>
		</>
	);
}
